import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react"; // Added useState
import { useParams,useNavigate } from "react-router-dom";
import MediaDisplay from "../../../app/common/controls/MediaDisplay";
import { Product } from "../../../app/models/product";
import { useStore } from "../../../app/stores/rootStore";

export default observer(function ProductDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { productsStore,cartStore,wishlistStore, userStore: { user } } = useStore();
    const { selectedItem,items,isLoaded, loadProduct, setItem,recommendedproducts } = productsStore;
    
    // State to manage the active gallery image
    const [selectedImage, setSelectedImage] = useState("");
    useEffect(() => {
        if (id) {
            loadProduct(id).then((p) => {
                if (p) {
                    setItem(p);
                    // Initialize the gallery with the main image
                    setSelectedImage(p.mainImageUrl);                    
                }
            });
        }
    }, [id, loadProduct, setItem,wishlistStore]);

    if (!selectedItem || selectedItem.id !== id) {
        navigate('/');
        return <div className="text-center mt-5">Loading Product Details...</div>;                
    }

    const product = selectedItem;
    // --- Recommendation Logic ---
    // Filter products that share the same brand but are not the current product    
    const isWishlisted = wishlistStore.isWishlisted(product.id!);

    const handleWishlistClick = async () => {
    const exists = wishlistStore.isWishlisted(product.id!);    
    await wishlistStore.toggleWishlist(user?.uid, product);
};

    return (
        <section className="product-detail-section py-5">
            <div className="container">
                <div className="row" style={{paddingTop:'90px'}}>
                    {/* --- Gallery Section --- */}
                    <div className="col-lg-6">
                        <div className="main-image shadow-sm rounded overflow-hidden mb-3">
                            {/* Display the locally selected image instead of always the main one */}
                            <MediaDisplay 
                                imageUrl={selectedImage || product.mainImageUrl} 
                                title={product.name} displayMode={product.displayMode}
                            />
                        </div>
                        <div className="thumbnail-gallery d-flex flex-wrap gap-2">
                            {/* Combine main image and gallery images into one selectable list */}
                            {[product.mainImageUrl, ...product.productImages].map((imgUrl, index) => (
                                <div 
                                    key={index} 
                                    className={`thumbnail-item border rounded p-1 ${selectedImage === imgUrl ? 'border-danger' : ''}`}
                                    style={{ cursor: 'pointer', transition: '0.2s' }}
                                    onClick={() => setSelectedImage(imgUrl)} // Switch main view on click
                                >
                                    {product.displayMode === 'video' ? (<video controls autoPlay={true} loop muted style={{ width: '80px', height: '80px', objectFit: 'cover' }}>
                                                    <source src={product.mainImageUrl} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>) : <img 
                                        src={imgUrl} 
                                        alt={`${product.name} thumbnail ${index}`} 
                                        className="img-fluid"
                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }} 
                                    />}
                                    
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* --- Details Section --- */}
                    <div className="col-lg-6">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-2">
                                <li className="breadcrumb-item text-uppercase small">{product.brand}</li>
                            </ol>
                        </nav>
                        <h1 className="h2 fw-bold">{product.title}</h1>
                        
                        <div className="price-area my-4 align-items-baseline">
                            <span className="h3 text-danger fw-bold">Rs. {product.productPrice.toLocaleString()}</span>
                            <span className="text-muted ms-3 text-decoration-line-through">
                                Rs. {product.productStrikePrice.toLocaleString()}
                            </span>
                        </div>

                        <p className="text-primary">{product.description}</p>

                        <div className="mt-4">
                            <h6 className="fw-bold">Key Features</h6>
                            <ul className="text-primary small">
                                {product.descriptionList.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="row mt-4">
                            <div className="col-6">
                                <h6 className="fw-bold small">Available Colors</h6>
                                <p className="small text-muted">{product.colors.join(', ')}</p>
                            </div>
                            <div className="col-6">
                                <h6 className="fw-bold small">Available Sizes</h6>
                                <p className="small text-muted">{product.sizes.join(', ')}</p>
                            </div>
                        </div>
                        
                        <div className="d-grid gap-2 d-md-flex mt-4">
                            <button className="btn btn-danger btn-lg px-5 shadow-sm" onClick={() => {
                                product.mainImageUrl = selectedImage;
                                cartStore.addToCart(user?.uid, product);
                                navigate(`/cart`);

                            }}>Add to Cart</button>
                            <button className="btn btn-outline-secondary btn-lg px-4" onClick={handleWishlistClick}>
                                <i className={`bi ${isWishlisted ? 'bi-heart-fill' : 'bi-heart'} h4 mb-0`}></i>Wishlist
                            </button>
                        </div>
                    </div>
                </div>
                {isLoaded && recommendedproducts.length > 0 && (
                    <div className="recommendations-section mt-5 pt-5 border-top">
                        <h3 className="fw-bold mb-4">You May Also Like</h3>
                        <div className="row row-cols-1 row-cols-md-4 g-4">
                            {recommendedproducts.map((rec) => (
                                <div key={rec.id} className="col">
                                    <div 
                                        className="card h-100 shadow-sm border-0" 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/product/${rec.id}`)}
                                    >
                                        <img 
                                            src={rec.mainImageUrl} 
                                            className="card-img-top" 
                                            alt={rec.title}
                                            style={{ objectFit: 'cover' }}
                                        />
                                        <div className="card-body">
                                            <p className="text-muted small mb-1 text-uppercase">{rec.brand}</p>
                                            <h6 className="card-title fw-bold text-truncate">{rec.title}</h6>
                                            <p className="text-danger fw-bold">Rs. {rec.productPrice.toLocaleString()}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
});